// src/components/groups/InvitationPageShell.jsx
// The one look for the invitation pages (Roadmap #54, Invitations Build 2 Phase 3): the personal
// invitation (AcceptInvitation), the shared join link (JoinGroupLink) and the email's opt-out and
// report pages (InvitationEmailAction). A centred white card on the page background, the OurNet
// logo, an optional group block, a title, the body, and an optional small print line.
// Presentational only — no data access. Groups mauve throughout: these pages are about a group,
// whatever context the visitor arrived in.

import React from 'react';
import { Users, Check, X, Clock } from 'lucide-react';
import { tokens } from '../../styles/designTokens';

const MAUVE = tokens.colors.contextGroups;

const STATUS_ICON = {
  success: { Icon: Check, color: tokens.colors.action, background: tokens.colors.actionDilute },
  error: { Icon: X, color: tokens.colors.destructive, background: '#FEF2F2' },
  waiting: { Icon: Clock, color: tokens.colors.textSecondary, background: tokens.colors.headerBackground },
};

/** The group's logo (or a placeholder), name, place and optional description. */
export const InvitationGroupBlock = ({ name, logoUrl, place, description }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', textAlign: 'left' }}>
    {logoUrl ? (
      <img
        src={logoUrl}
        alt=""
        aria-hidden="true"
        style={{ width: '64px', height: '64px', borderRadius: '12px', objectFit: 'cover', flexShrink: 0 }}
      />
    ) : (
      <div
        aria-hidden="true"
        style={{
          width: '64px', height: '64px', borderRadius: '12px', flexShrink: 0,
          background: tokens.colors.headerBackground,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}
      >
        <Users size={28} color={MAUVE} />
      </div>
    )}
    <div style={{ minWidth: 0 }}>
      {name && <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1F2937' }}>{name}</h2>}
      {place && <p style={{ margin: '2px 0 0', fontSize: '14px', color: tokens.colors.textSecondary }}>{place}</p>}
      {description && (
        <p style={{
          margin: '6px 0 0', fontSize: '13px', color: tokens.colors.textSecondary, lineHeight: 1.4,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
        }}>
          {description}
        </p>
      )}
    </div>
  </div>
);

/** Small grey text line, e.g. the expiry date or "you were invited as …". */
export const InvitationNote = ({ children, icon = null }) => (
  <p style={{
    display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-start',
    margin: '0 0 12px', fontSize: '13px', color: tokens.colors.textSecondary, textAlign: 'left'
  }}>
    {icon}
    <span>{children}</span>
  </p>
);

/** A column of buttons, full width. */
export const InvitationActions = ({ children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', marginTop: '20px' }}>
    {children}
  </div>
);

const InvitationPageShell = ({ status = null, title = null, loading = false, smallPrint = null, children }) => {
  const statusIcon = status ? STATUS_ICON[status] : null;
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: tokens.colors.headerBackground, padding: '20px 16px', boxSizing: 'border-box'
    }}>
      <div style={{
        background: tokens.colors.contentBar, borderRadius: '16px', padding: '32px 24px',
        maxWidth: '480px', width: '100%', boxSizing: 'border-box', textAlign: 'center',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
      }}>
        <img
          src="/images/logos/logo-ournet.png"
          alt="OurNet"
          style={{ height: '40px', width: 'auto', margin: '0 auto 24px', display: 'block' }}
        />
        {loading ? (
          <div
            role="status"
            style={{
              width: '40px', height: '40px', margin: '8px auto',
              border: `4px solid ${tokens.colors.disabledBackground}`, borderTopColor: MAUVE,
              borderRadius: '50%', animation: 'invitation-spin 1s linear infinite'
            }}
          >
            <style>{'@keyframes invitation-spin { to { transform: rotate(360deg); } }'}</style>
          </div>
        ) : (
          <>
            {statusIcon && (
              <div style={{
                width: '56px', height: '56px', borderRadius: '50%', background: statusIcon.background,
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px'
              }}>
                <statusIcon.Icon size={28} color={statusIcon.color} />
              </div>
            )}
            {title && <h1 style={{ margin: '0 0 16px', fontSize: '20px', fontWeight: 700, color: '#1F2937' }}>{title}</h1>}
            {children}
          </>
        )}
        {smallPrint && (
          <p style={{ margin: '24px 0 0', fontSize: '12px', color: tokens.colors.disabledText }}>{smallPrint}</p>
        )}
      </div>
    </div>
  );
};

/** Body text paragraph inside the card. */
export const InvitationText = ({ children }) => (
  <p style={{ margin: '0 0 12px', fontSize: '15px', color: tokens.colors.textSecondary, lineHeight: 1.5 }}>
    {children}
  </p>
);

export default InvitationPageShell;
